import React, { useId, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDown } from 'lucide-react';

/**
 * Collapsible technical-detail section on the sponsor result card (scope §6.4).
 *
 * Expands the card in place rather than opening a modal, and animates height so the cards below
 * slide rather than jump. Collapsed by default: a sponsor who does not need the technical detail
 * sees essentially the card they saw before.
 *
 * Shared by the Primary Capabilities and Manufacturing Equipment sections so the two cannot drift
 * apart in behaviour or timing.
 */

export interface CardDetailAccordionProps {
  /** Small leading icon, coloured by the caller to match the row it belongs to. */
  icon: React.ReactNode;
  title: string;
  /** Collapsed-state summary shown after the title, e.g. "· 11" or "· 6 items · 3 facilities". */
  summary: string;
  children: React.ReactNode;
}

/** §6.4 asks for roughly 200-300 ms; 250 ms sits in the middle and matches the card's other motion. */
/**
 * Scope §6.4 asks for roughly 200-300ms. Exported so the band is asserted in a test rather than
 * left to a wall-clock measurement, which on a long results list reads high purely because every
 * mounted card re-lays out around the one that grew.
 */
export const EXPAND_SECONDS = 0.25;

const CardDetailAccordion: React.FC<CardDetailAccordionProps> = ({
  icon,
  title,
  summary,
  children,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const panelId = useId();

  return (
    <div className="border border-slate-gray/20 rounded-xl bg-light-gray/40 overflow-hidden">
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        aria-expanded={isOpen}
        aria-controls={panelId}
        className="w-full flex items-center justify-between gap-3 px-3.5 py-2.5 text-left hover:bg-electric-blue/5 transition-colors cursor-pointer"
      >
        <span className="flex items-center gap-2 min-w-0">
          <span className="shrink-0 flex" aria-hidden="true">{icon}</span>
          <span className="text-sm font-semibold text-charcoal">{title}</span>
          <span className="text-xs text-slate-gray whitespace-nowrap">{summary}</span>
        </span>
        <ChevronDown
          className={`w-4 h-4 shrink-0 text-slate-gray transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          aria-hidden="true"
        />
      </button>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            id={panelId}
            key="panel"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: EXPAND_SECONDS, ease: 'easeInOut' }}
            // Height is animated rather than display toggled, so neighbouring cards in the results
            // grid slide instead of snapping.
            style={{ overflow: 'hidden' }}
          >
            <div className="px-3.5 pb-3.5 pt-0.5">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CardDetailAccordion;
